<template>
  <v-dialog v-model="showing" width="100%" max-width="500px">
    <v-form @submit.prevent="save()">
      <div class="afd-root">

        <!-- Header -->
        <div class="afd-header">
          <div>
            <div class="afd-title">Advanced search</div>
            <div class="afd-subtitle">Filter by follower demographics</div>
          </div>
          <button type="button" class="afd-close" @click="cancel()">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="afd-body">

          <!-- Follower location -->
          <div class="afd-section">
            <div class="afd-section-title">
              <i class="fal fa-map-marker-alt afd-section-icon"></i>
              Follower location
            </div>
            <div class="afd-section-desc">Choose which cities the influencers' followers should be located in</div>
            <city-select v-model="internalFilters.cities"></city-select>
          </div>

          <!-- Follower age & gender -->
          <div class="afd-section">
            <div class="afd-section-title">
              <i class="fal fa-users afd-section-icon"></i>
              Follower age & gender
            </div>
            <div class="afd-section-desc">Choose influencer's follower age and gender distribution</div>
            <age-gender-select v-model="internalFilters.age_genders"></age-gender-select>
          </div>

        </div>

        <!-- Footer -->
        <div class="afd-footer">
          <button type="button" class="afd-btn-cancel" @click="cancel()">Cancel</button>
          <div class="afd-footer-right">
            <subscription-crown-icon v-if="!hasSubscription" :size="12" class="mr-2" />
            <button
              type="submit"
              class="afd-btn-apply"
              :class="{ 'afd-btn-apply--disabled': !hasSubscription }"
              :disabled="!hasSubscription"
              @click="save()"
            >
              <i class="fal fa-check" style="margin-right:6px;font-size:12px"></i>
              Apply filters
            </button>
          </div>
        </div>

      </div>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import CitySelect from "../selects/CitySelect";
import AgeGenderSelect from "../selects/AgeGenderSelect";
import SubscriptionCrownIcon from "../business/icon/SubscriptionCrownIcon";

export default {
  components: { SubscriptionCrownIcon, AgeGenderSelect, CitySelect },
  props: {
    value: { type: Boolean, required: false, default: false },
    filters: { required: true, type: Object }
  },
  data: () => ({ internalFilters: null }),
  computed: {
    ...mapState("core/auth", ["user"]),
    ...mapGetters("subscriptions", ["hasSubscription"]),
    showing: {
      get() { return this.value; },
      set(value) { this.$emit("input", value); }
    }
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(newVal) { this.internalFilters = this.cloneDeep(newVal); }
    }
  },
  methods: {
    save() {
      const params = ["category_ids", "cities", "age_genders"];
      params.forEach(param => {
        if (param in this.internalFilters && this.internalFilters[param].length === 0) {
          delete this.internalFilters[param];
        }
      });
      this.$emit("saved", this.cloneDeep(this.internalFilters));
      this.showing = false;
    },
    cancel() { this.showing = false; }
  }
};
</script>

<style lang="scss" scoped>
.afd-root {
  background: var(--surface, #fff);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.afd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--line, #eaecf0);
}

.afd-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink-1, #111);
}

.afd-subtitle {
  font-size: 13px;
  color: var(--ink-3, #aaa);
  margin-top: 2px;
}

.afd-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--surface-2, #f5f5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: var(--ink-2, #555);
  flex-shrink: 0;
  &:hover { background: var(--line, #eaecf0); }
}

/* ── Body ── */
.afd-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.afd-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.afd-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-1, #111);
}

.afd-section-icon {
  color: var(--accent, #e05540);
  font-size: 14px;
}

.afd-section-desc {
  font-size: 12px;
  color: var(--ink-3, #aaa);
  margin-bottom: 4px;
}

/* ── Footer ── */
.afd-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid var(--line, #eaecf0);
}

.afd-footer-right {
  display: flex;
  align-items: center;
}

.afd-btn-cancel {
  background: none;
  border: 1px solid var(--line, #eaecf0);
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  color: var(--ink-2, #555);
  cursor: pointer;
  &:hover { background: var(--surface-2, #f5f5f5); }
}

.afd-btn-apply {
  background: var(--accent, #e05540);
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
  &--disabled { opacity: 0.4; cursor: not-allowed; }
}
</style>